import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { auth, storage } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getDownloadURL, ref } from 'firebase/storage';

// ===== 임시 접근 제한 설정 (공지사항 비공개용) =====
// TODO: 공지사항을 다시 일반 사용자에게 공개할 때 false로 변경하세요
const TEMPORARY_NOTICE_ACCESS_CONTROL = false;
const MAX_ALLOWED_ADMIN_USER_LEVEL = 2;
// ==============================================

interface Notice {
  id: string;
  title: string;
  content: string;
  isSecret?: boolean;
  attachments?: Array<{
    id: string;
    name: string;
    storagePath: string;
    size: number;
    contentType: string;
    uploadedAt: any;
  }>;
  createdAt: any;
  updatedAt: any;
}

const NoticeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [notice, setNotice] = useState<Notice | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(!TEMPORARY_NOTICE_ACCESS_CONTROL);
  const [isAdminViewer, setIsAdminViewer] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        setIsAdminViewer(false);
        setAdminCheckComplete(true);
        return;
      }

      try {
        const userProfileRef = doc(db, 'user_profiles', authUser.uid);
        const userProfileSnap = await getDoc(userProfileRef);
        const userLevel = userProfileSnap.exists() ? userProfileSnap.data().userLevel ?? 999 : 999;

        setIsAdminViewer(userLevel <= 2);
      } catch (error) {
        console.error('관리자 권한 확인 실패:', error);
        setIsAdminViewer(false);
      } finally {
        setAdminCheckComplete(true);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!TEMPORARY_NOTICE_ACCESS_CONTROL) {
      setHasAccess(true);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        navigate('/signin');
        return;
      }

      try {
        const userProfileRef = doc(db, 'user_profiles', authUser.uid);
        const userProfileSnap = await getDoc(userProfileRef);

        if (!userProfileSnap.exists()) {
          navigate('/site-preparation');
          return;
        }

        const userLevel = userProfileSnap.data().userLevel ?? 999;

        if (userLevel > MAX_ALLOWED_ADMIN_USER_LEVEL) {
          console.log(`공지사항 상세 접근 제한: userLevel ${userLevel} > ${MAX_ALLOWED_ADMIN_USER_LEVEL}`);
          navigate('/site-preparation');
          return;
        }

        setHasAccess(true);
      } catch (error) {
        console.error('공지사항 상세 접근 권한 확인 실패:', error);
        navigate('/site-preparation');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!hasAccess || !adminCheckComplete) {
      return;
    }

    const fetchNotice = async () => {
      if (!id) return;
      
      try {
        const docRef = doc(db, 'notices', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setNotice({
            id: docSnap.id,
            ...docSnap.data(),
            attachments: Array.isArray(docSnap.data().attachments) ? docSnap.data().attachments : []
          } as Notice);
        }
      } catch (error) {
        console.error('공지사항을 불러오는데 실패했습니다:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotice();
  }, [hasAccess, adminCheckComplete, id]);

  const handleAttachmentDownload = async (storagePath: string, fileName: string) => {
    try {
      const downloadURL = await getDownloadURL(ref(storage, storagePath));
      const response = await fetch(downloadURL);

      if (!response.ok) {
        throw new Error('파일 응답을 가져오지 못했습니다.');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('첨부파일 다운로드 실패:', error);
      window.alert('첨부파일을 다운로드하지 못했습니다.');
    }
  };

  const isHiddenSecretNotice = notice?.isSecret === true && !isAdminViewer;
  const attachments = Array.isArray(notice?.attachments) ? notice!.attachments! : [];

  return (
    <div className="w-full min-h-screen bg-slate-50">
      {/* Header */}
      <div className="w-full h-20 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.01)]">
        <div className="max-w-[1920px] mx-auto px-4 h-full relative flex items-center">
          <Link to="/" className="md:block hidden">
            <div className="w-auto h-8 left-[40px] top-[26px] absolute whitespace-nowrap text-[#1A253F] text-[14px] md:text-[28px] font-extrabold font-pretendard leading-9">
              부산항신항시설관리센터
            </div>
          </Link>
          {/* Mobile Header */}
          <Link to="/" className="md:hidden block w-full">
            <div className="w-full h-7 text-center text-[#1A253F] text-[18px] md:text-[28px] font-extrabold font-pretendard leading-9">
              부산항신항시설관리센터
            </div>
          </Link>
        </div>
      </div>
      <div className="w-full h-px bg-white border border-slate-200"></div>

      {/* Main Content - 다른 페이지들과 동일한 디자인 */}
      <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-16 py-6 md:py-12">
        <div className="w-[calc(100%-32px)] md:w-[760px] mx-auto relative">
          {/* 헤더 섹션 */}
          <div className="mt-4 md:mt-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
              <div className="text-slate-800 text-2xl md:text-4xl font-extrabold font-['Pretendard'] leading-9">
                공지사항
              </div>
              <Link
                to="/notices"
                className="text-[#6b7684] hover:text-[#1e2c49] transition-colors text-sm md:text-lg font-medium"
              >
                목록으로 돌아가기
              </Link>
            </div>
            <div className="w-full h-px mt-6 md:mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
          </div>

          {/* 본문 내용 */}
          <div className="mt-6 md:mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-4 md:p-8">
            {loading ? (
              <div className="bg-[#f2f6fb] p-4 md:p-6 rounded-sm text-center">
                <p className="text-[#6b7684] text-base md:text-lg font-medium">공지사항을 불러오는 중입니다...</p>
              </div>
            ) : notice && !isHiddenSecretNotice ? (
              <div>
                {/* 공지사항 제목 및 정보 */}
                <div className="mb-6 md:mb-8">
                  <h2 className="text-[#1e2c49] text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-3 md:mb-4">
                    {notice.title}
                    {notice.isSecret === true && isAdminViewer && (
                      <span className="ml-2 inline-flex rounded-full bg-red-100 px-2 py-0.5 align-middle text-xs font-semibold text-red-700">
                        비밀
                      </span>
                    )}
                  </h2>
                  <div className="w-full md:w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-4 md:mb-6"></div>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 md:gap-0 mb-4 md:mb-6">
                    <span className="text-[#6b7684] text-sm md:text-lg font-medium">작성일</span>
                    <span className="text-[#1e2c49] text-base md:text-[19px] font-semibold">
                      {notice.createdAt.toDate().toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>

                {/* 공지사항 내용 */}
                <div className="mb-6 md:mb-8">
                  <h3 className="text-[#1e2c49] text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-3 md:mb-4">
                    내용
                  </h3>
                  <div className="w-full md:w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-4 md:mb-6"></div>
                  <div className="bg-[#f2f6fb] rounded-sm p-4 md:p-6">
                    <div
                      className="text-[#1e2c49] text-base md:text-[19px] font-medium leading-relaxed prose prose-slate max-w-none"
                      dangerouslySetInnerHTML={{ __html: notice.content }}
                      style={{
                        color: '#1e2c49',
                        fontSize: 'inherit',
                        fontWeight: '500',
                        lineHeight: '1.6'
                      }}
                    />
                  </div>
                </div>

                {attachments.length > 0 && (
                  <div className="mb-6 md:mb-8">
                    <h3 className="text-[#1e2c49] text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-3 md:mb-4">
                      첨부파일
                    </h3>
                    <div className="w-full md:w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-4 md:mb-6"></div>
                    <div className="bg-[#f2f6fb] rounded-sm p-4 md:p-6">
                      <div className="space-y-3">
                        {attachments.map((attachment) => (
                          <button
                            key={attachment.id}
                            type="button"
                            onClick={() => handleAttachmentDownload(attachment.storagePath, attachment.name)}
                            className="flex w-full items-center justify-between rounded-sm border border-slate-200 bg-white px-4 py-3 text-left hover:bg-slate-50"
                          >
                            <span className="truncate pr-4 text-[#1e2c49] text-sm md:text-base font-medium">
                              {attachment.name}
                            </span>
                            <span className="text-[#6b7684] text-xs md:text-sm whitespace-nowrap">
                              {(attachment.size / 1024 / 1024).toFixed(2)}MB
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#f2f6fb] p-4 md:p-6 rounded-sm text-center">
                <h3 className="text-[#1e2c49] text-base md:text-lg font-semibold mb-3 md:mb-4">
                  공지사항을 찾을 수 없습니다
                </h3>
                <p className="text-[#6b7684] text-base md:text-lg font-medium">
                  존재하지 않거나 접근할 수 없는 공지사항입니다.
                </p>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* 푸터 */}
      <footer className="bg-[#0E2039] h-[340px] md:h-[210px] mt-[100px]">
        <div className="max-w-[1380px] mx-auto px-4 sm:px-8 md:px-16 pt-[40px] md:pt-[54px]">
          <div className="flex flex-col md:flex-row justify-between">
            <div>
              <h4 className="text-[16px] md:text-[24px] font-semibold text-white/80 font-pretendard mb-[41px]">
                부산항신항시설관리센터
              </h4>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-[8px] md:gap-[24px]">
                <p className="text-[14px] md:text-[15px] font-medium text-[#64AAC6] font-pretendard">Contact</p>
                <p className="text-[12px] font-light text-white/40 font-pretendard">
                  부산광역시 강서구 신항로 96-87
                </p>
                <div className="flex items-center gap-[8px]">
                  <p className="text-[12px] font-light text-white/40 font-pretendard">
                    대표번호:051-890-7400
                  </p>
                  <p className="text-[12px] font-light text-white/40 font-pretendard">
                    FAX:051-831-5344
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-[8px] md:gap-[44px] mt-[20px] md:mt-0">
              <Link to="/notices" className="text-[14px] md:text-[15px] font-bold text-white font-pretendard">공지사항</Link>
              <Link to="/privacy" className="text-[14px] md:text-[15px] font-bold text-white font-pretendard">개인정보처리방침</Link>
              <Link to="/email-policy" className="text-[14px] md:text-[15px] font-bold text-white font-pretendard">이메일무단수집거부</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NoticeDetailPage; 
